<template>
  <div class="flex h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
    <!-- Mobile Sidebar Backdrop -->
    <div 
      v-if="uiStore.sidebar.isOpen" 
      @click="uiStore.toggleSidebar()"
      class="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity duration-300"
    ></div>

    <!-- Sidebar -->
    <aside 
      class="fixed inset-y-0 left-0 z-50 lg:static lg:inset-auto bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ease-in-out flex flex-col transform"
      :class="[
        uiStore.sidebar.collapsed ? 'w-16' : 'w-64',
        uiStore.sidebar.isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      ]"
    >
      <!-- Sidebar Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <router-link v-if="!uiStore.sidebar.collapsed || uiStore.sidebar.isOpen" to="/" class="flex items-center overflow-hidden">
          <img src="@/assets/logo/logo_horizntal.png" alt="Ruxzom Logo" class="h-8 min-w-[120px]" />
        </router-link>

        <button @click="uiStore.toggleSidebarCollapsed()"
          class="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
          <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              :d="uiStore.sidebar.collapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'"></path>
          </svg>
        </button>

        <!-- Mobile Close Button -->
        <button @click="uiStore.toggleSidebar()"
          class="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200">
          <svg class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Navigation -->
      <nav class="p-4 flex-1 overflow-y-auto custom-scrollbar">
        <ul class="space-y-2">
          <li v-for="item in navigationItems" :key="item.name">
            <!-- Menu item with submenu -->
            <div v-if="item.submenu">
              <button @click="toggleSubmenu(item.name)"
                class="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                :class="{ 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400': isSubmenuActive(item) }">
                <div class="flex items-center">
                  <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
                  <span v-if="!uiStore.sidebar.collapsed || uiStore.sidebar.isOpen" class="ml-3 font-medium">
                    {{ item.name }}
                  </span>
                </div>
                <svg v-if="!uiStore.sidebar.collapsed || uiStore.sidebar.isOpen" class="w-4 h-4 transition-transform duration-200"
                  :class="{ 'transform rotate-180': expandedMenus.includes(item.name) }" fill="none"
                  stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              <!-- Submenu items -->
              <ul v-if="(!uiStore.sidebar.collapsed || uiStore.sidebar.isOpen) && expandedMenus.includes(item.name)" class="mt-2 ml-8 space-y-1">
                <li v-for="subitem in item.submenu" :key="subitem.name">
                  <router-link :to="subitem.href"
                    class="block px-3 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                    :class="{ 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium': $route.path === subitem.href }">
                    {{ subitem.name }}
                  </router-link>
                </li>
              </ul>
            </div>

            <!-- Regular menu item without submenu -->
            <router-link v-else :to="item.href"
              class="flex items-center px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
              :class="{ 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border-r-2 border-primary-600': $route.path === item.href }">
              <component :is="item.icon" class="w-5 h-5 flex-shrink-0" />
              <span v-if="!uiStore.sidebar.collapsed || uiStore.sidebar.isOpen" class="ml-3 font-medium">
                {{ item.name }}
              </span>
            </router-link>
          </li>
        </ul>
      </nav>

      <!-- User Info -->
      <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0">
            <span class="text-primary-600 dark:text-primary-400 font-medium text-sm">
              {{ authStore.user?.name?.charAt(0) }}
            </span>
          </div>
          <div v-if="!uiStore.sidebar.collapsed || uiStore.sidebar.isOpen" class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
              {{ authStore.user?.name }}
            </p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate uppercase font-bold tracking-widest">
              {{ authStore.user?.role }} Account
            </p>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- Top Header -->
      <header class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4 transition-colors duration-300">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <!-- Mobile Menu Toggle -->
            <button @click="uiStore.toggleSidebar()"
              class="mr-4 lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7"></path>
              </svg>
            </button>
            
            <div class="max-w-[150px] sm:max-w-none">
              <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate uppercase tracking-tight" style="font-family: Poppins, sans-serif">
                {{ title || pageTitle }}
              </h1>
              <p class="hidden sm:block text-sm text-gray-600 dark:text-gray-400">{{ description || pageDescription }}</p>
            </div>
          </div>

          <div class="flex items-center space-x-2 sm:space-x-4">
            <!-- Theme Toggle -->
            <button 
              @click="uiStore.setTheme(uiStore.theme === 'dark' ? 'light' : 'dark')"
              class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors duration-300"
              :title="uiStore.theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'"
            >
              <svg v-if="uiStore.theme === 'dark'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M12 5a7 7 0 100 14 7 7 0 000-14z"></path>
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"></path>
              </svg>
            </button>

            <!-- Notifications -->
            <button class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 relative">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15 17h5l-5 5v-5zM10.07 2.82l-.9.9a4 4 0 000 5.66l.9.9a6 6 0 010 8.49l-.9.9a4 4 0 000 5.66l.9.9">
                </path>
              </svg>
              <span class="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-400"></span>
            </button>

            <!-- User Menu -->
            <div class="relative" ref="userMenuRef">
              <button @click="showUserMenu = !showUserMenu"
                class="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white focus:outline-none">
                <div class="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                  <span class="text-primary-600 dark:text-primary-400 font-medium text-sm">
                    {{ authStore.user?.name?.charAt(0) }}
                  </span>
                </div>
                <span class="hidden md:block font-medium">{{ authStore.user?.name }}</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              <!-- Dropdown Menu -->
              <div v-show="showUserMenu"
                class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                <router-link :to="authStore.user?.role === 'admin' ? '/dashboard/admin/profile' : (authStore.user?.role === 'developer' ? '/dashboard/developer/profile' : '/dashboard/client/profile')" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="showUserMenu = false">
                  Profile Settings
                </router-link>
                <router-link to="/" class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  @click="showUserMenu = false">
                  View Website
                </router-link>
                <hr class="my-1 border-gray-200 dark:border-gray-700">
                <button @click="handleLogout"
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 custom-scrollbar">
        <slot />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'

const route = useRoute()
const authStore = useAuthStore()
const uiStore = useUIStore()

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  }
})

// State
const showUserMenu = ref(false)
const userMenuRef = ref(null)
const expandedMenus = ref(['Content Management']) // Default expand Content Management

// Methods for submenu handling
const toggleSubmenu = (menuName) => {
  const index = expandedMenus.value.indexOf(menuName)
  if (index > -1) {
    expandedMenus.value.splice(index, 1)
  } else {
    expandedMenus.value.push(menuName)
  }
}

const isSubmenuActive = (item) => {
  if (!item.submenu) return false
  return item.submenu.some(subitem => route.path === subitem.href)
}

// Navigation items based on user role
const navigationItems = computed(() => {
  const baseItems = []

  if (authStore.user?.role === 'admin') {
    return [
      { name: 'Dashboard', href: '/dashboard/admin', icon: 'DashboardIcon' },
      {
        name: 'Platform Assets',
        icon: 'ContentIcon',
        submenu: [
          { name: 'Home View', href: '/dashboard/admin/content/home' },
          { name: 'Service Portfolio', href: '/dashboard/admin/content/services' },
          { name: 'Portfolio Showcase', href: '/dashboard/admin/content/portfolio' },
          { name: 'About Identity', href: '/dashboard/admin/content/about' },
          { name: 'Inquiry System', href: '/dashboard/admin/content/contact' }
        ]
      },
      { name: 'Expert Profiles', href: '/dashboard/admin/content/dev-profiles', icon: 'ProfileIcon' },
      { name: 'Product Base', href: '/dashboard/admin/products', icon: 'ProductsIcon' },
      { name: 'Technical Lab', href: '/dashboard/admin/dev-assistant', icon: 'CodeIcon' },
      { name: 'Transaction History', href: '/dashboard/admin/orders', icon: 'OrdersIcon' },
      { name: 'Operations Log', href: '/dashboard/admin/activity-logs', icon: 'ClockIcon' },
      { name: 'Compliance', href: '/dashboard/admin/legal-pages', icon: 'LegalIcon' },
      { name: 'Member Directory', href: '/dashboard/admin/users', icon: 'UsersIcon' },
      { name: 'System Settings', href: '/dashboard/admin/settings', icon: 'SettingsIcon' }
    ]
  } else if (authStore.user?.role === 'developer') {
    const items = [
      { name: 'Dashboard', href: '/dashboard/developer', icon: 'DashboardIcon' },
      { name: 'Dev Assistant', href: '/dashboard/admin/dev-assistant', icon: 'CodeIcon' }
    ]

    const contentSubmenu = []
    if (authStore.hasPermission('access_dev_content')) {
      contentSubmenu.push({ name: 'Home', href: '/dashboard/admin/content/home' })
      contentSubmenu.push({ name: 'Services', href: '/dashboard/admin/content/services' })
      contentSubmenu.push({ name: 'Portfolio', href: '/dashboard/admin/content/portfolio' })
      contentSubmenu.push({ name: 'About', href: '/dashboard/admin/content/about' })
    }

    

    if (authStore.hasPermission('manage_dev_profiles')) {
      items.push({ name: 'Dev Profiles', href: '/dashboard/developer/profile', icon: 'ProfileIcon' })
    }

    if (authStore.hasPermission('manage_products')) {
      items.push({ name: 'Products', href: '/dashboard/developer/products', icon: 'ProductsIcon' })
    }

    if (authStore.hasPermission('manage_orders')) {
      items.push({ name: 'Orders', href: '/dashboard/developer/orders', icon: 'OrdersIcon' })
    }

    if (authStore.hasPermission('manage_users')) {
      items.push({ name: 'Users', href: '/dashboard/admin/users', icon: 'UsersIcon' })
    }

    if (authStore.hasPermission('access_activity_logs')) {
      items.push({ name: 'Activity Logs', href: '/dashboard/admin/activity-logs', icon: 'ClockIcon' })
    }

    if (authStore.hasPermission('manage_settings')) {
      items.push({ name: 'Settings', href: '/dashboard/admin/settings', icon: 'SettingsIcon' })
    }

    return items
  }
 else if (authStore.user?.role === 'client') {
    return [
      { name: 'Dashboard', href: '/dashboard/client', icon: 'DashboardIcon' },
      { name: 'Order Service', href: '/order-service', icon: 'OrderIcon' },
      { name: 'My Orders', href: '/dashboard/client/orders', icon: 'OrdersIcon' },
      { name: 'Profile', href: '/dashboard/client/profile', icon: 'ProfileIcon' }
    ]
  }

  return baseItems
})

// Page title and description
const pageTitle = computed(() => {
  const routeNames = {
    '/dashboard/admin': 'Admin Dashboard',
    '/dashboard/developer': 'Developer Dashboard',
    '/dashboard/client': 'Client Dashboard',
    '/dashboard/admin/content/home': 'Home Content',
    '/dashboard/admin/content/services': 'Services Content',
    '/dashboard/admin/content/portfolio': 'Portfolio Content',
    '/dashboard/admin/content/about': 'About Content',
    '/dashboard/admin/content/contact': 'Contact Content',
    '/dashboard/admin/content/dev-profiles': 'Dev Profiles Management',
    '/dashboard/admin/services': 'Service Management',
    '/dashboard/admin/orders': 'Order Management',
    '/dashboard/admin/users': 'User Management',
    '/dashboard/admin/settings': 'Settings',
    '/dashboard/developer/orders': 'Assigned Orders',
    '/dashboard/developer/products': 'Assigned Products',
    '/dashboard/developer/profile': 'Dev Profile Settings',
    '/dashboard/client/orders': 'Order History',
    '/dashboard/client/profile': 'Profile Settings',
    '/order-service': 'Order Service'
  }
  return routeNames[route.path] || 'Dashboard'
})

const pageDescription = computed(() => {
  const descriptions = {
    '/dashboard/admin': 'Overview of your business metrics and recent activity',
    '/dashboard/developer': 'Overview of development metrics and assigned tasks',
    '/dashboard/client': 'Your project overview and recent activity',
    '/dashboard/admin/orders': 'Track and manage client orders',
    '/dashboard/admin/users': 'Manage user accounts and permissions',
    '/dashboard/admin/settings': 'System settings and configuration',
    '/dashboard/developer/orders': 'Manage your assigned development tasks',
    '/dashboard/developer/products': 'Review products you are developing',
    '/dashboard/client/orders': 'View your order history and status',
    '/dashboard/client/profile': 'Manage your account settings',
    '/order-service': 'Place a new service order'
  }
  return descriptions[route.path] || 'Manage your account'
})

// Methods
const handleLogout = () => {
  showUserMenu.value = false
  authStore.logout()
}

// Close dropdowns when clicking outside
const handleClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<script>
// Icon components
export default {
  components: {
    DashboardIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2v0"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5v6m8-6v6m-8 4h8"></path>
        </svg>
      `
    },
    ContentIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      `
    },
    ServicesIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
      `
    },
    OrdersIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
        </svg>
      `
    },
    UsersIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
        </svg>
      `
    },
    SettingsIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
        </svg>
      `
    },
    OrderIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path>
        </svg>
      `
    },
    LegalIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path>
        </svg>
      `
    },
    ProfileIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
        </svg>
      `
    },
    ProductsIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
        </svg>
      `
    },
    CodeIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"></path>
        </svg>
      `
    }
  }
}
</script>
